class navigation {
  constructor(id) {
    this.header = document.querySelector( '[role=banner]' );
    this.controller = this.header.querySelector( `[aria-controls="${id}"]` );

    this.isOpen = false; // Default state of the navigation.
    this.navigations = Array.from( this.header.getElementsByTagName( `nav` ) );
    this.activeSubmenuToggle = null;
    this.subMenuToggles = [];
    this.subMenuItems = [];
    this.topLevelItems = [];
    this.subMenuIsOpen = false;

    this.classList = {
      megaMenu: 'is-mega',
      simpleMenu: 'is-simple', // VRGZ doesn't use this, added for the starter theme.
    };

    this.bindMethods();
    this.init();
    this.windowEvents();
    this.clickEvents();
  }

  /**
   * Bind methods to the class.
   * THIS SHOULD ONLY BE DONE ONCE!
   */
  bindMethods() {
    // Bind methods to class and not the clickedElement.
    this.keyDownHandler = this.keyDownHandler.bind( this );
    this.setFocusTrap = this.setFocusTrap.bind( this );
    this.toggleSubMenu = this.toggleSubMenu.bind( this );
    this.openSubMenu = this.openSubMenu.bind( this );
    this.controllerKeyDownHandler = this.controllerKeyDownHandler.bind( this );
    this.updateController = this.updateController.bind( this );
    this.getZoomLevel = this.getZoomLevel.bind( this );
    this.getActiveSubmenuToggle = this.getActiveSubmenuToggle.bind( this );
    this.clickEvents = this.clickEvents.bind( this );
  }

  init() {
    // On load.
    this.isMobile = this.checkIfIsMobile();
    this.displaysNavigations = this.checkIfNavigationsAreDisplayed();
    this.zoomLevel = this.getZoomLevel();
    this.activeSubmenuToggle = this.getActiveSubmenuToggle();
    this.setTopLevelItems();
    this.updateTabIndex();
    this.keyboardEvents();

    if( ! this.isMobile && this.controller.ariaExpanded === 'true' ) {
      // Probably zooming out on desktop, therefor the navigation should be closed.
      this.isOpen = false;
      this.updateController( false );
    }
  }

  /**
   * All window events.
   */
  windowEvents() {
    const ww = window.innerWidth;

    // Zoom will be detected as resize event.
    window.addEventListener( 'resize', this.debounce( () => {
      if (ww === window.innerWidth && this.zoomLevel !== this.getZoomLevel() ) return; // Small hack for mobile devices which fire resize event on scroll.
      this.init();
    } ) );

    window.addEventListener( 'orientationchange', this.debounce( this.init ) );

    // Assign a method to this customEvent. Do not remove the `()=> ..()` syntax, without it, otherwise it won't work.
    // Fire the event with: `window.dispatchEvent( new CustomEvent( "closeNavigation" ) );`
    window.addEventListener( 'closeNavigation', (event) => {
      const {focus} = event.detail;
      this.closeNavigation( focus );
    } );
    window.addEventListener( 'openNavigation', () => this.openNavigation( false ) );
    window.addEventListener( 'openNavigationWithFocusTrap', () => this.openNavigation( true ) );
    window.addEventListener( 'reInitNavigation', () => this.init() );
  }

  clickEvents() {
    this.controller?.addEventListener( 'click', () => {
      this.toggleNavigation( true )
    } );
    this.subMenuToggles?.forEach( toggle => toggle.addEventListener( 'click', this.toggleSubMenu ) );
  }

  keyboardEvents() {
    this.topLevelItems?.forEach( item => item.addEventListener( 'keydown', (event) => this.keyDownHandler( event, 0 ) ) );
    this.controller?.addEventListener( 'keydown', this.controllerKeyDownHandler );

    this.initSubmenuKeyboardEvents();
  }

  /**
   * Add eventListeners to the submenu items.
   * Also used when new submenu-items array is populated.
   */
  initSubmenuKeyboardEvents() {
    this.subMenuItems?.forEach( item => item.addEventListener( 'keydown', (event) => this.keyDownHandler( event, 1 ) ) );
  }

  /**
   * Remove eventListeners to prevent duplication.
   */
  deleteSubmenuKeyboardEvents() {
    this.subMenuItems?.forEach( item => item.removeEventListener( 'keydown', (event) => this.keyDownHandler( event, 1 ) ) );
  }

  /**
   * Debounce function to limit the number of times a function is called.
   *
   * @param {function} func The function to debounce.
   * @param {number} delay The delay in microseconds.
   *
   * @return {(function(...[*]): void)|*}
   */
  debounce(func, delay = 250) {
    let timeout;
    return (...args) => {
      clearTimeout( timeout );
      timeout = setTimeout( () => func.apply( this, args ), delay );
    };
  }

  /**
   * Check if the navigation is mobile.
   *
   * @return {boolean}
   */
  checkIfIsMobile() {
    return window.getComputedStyle( this.controller ).display !== 'none';
  }

  /**
   * Returns an array of booleans for each navigation.
   *
   * @return {boolean[]}
   */
  checkIfNavigationsAreDisplayed() {
    if (!this.navigations.length) return [];
    return this.navigations?.map( nav => window.getComputedStyle( nav ).display !== 'none' );
  }

  /**
   * Get the navigation siblings.
   *
   * @param {HTMLElement} navigationSibling The navigation sibling, will filter out any other nav-elements.
   * @return {*[]}
   */
  getNavigationSiblings(navigationSibling) {
    if (!navigationSibling || 'NAV' === navigationSibling.nodeName) return [];
    return navigationSibling.querySelectorAll( ':scope > a[href], :scope > button[aria-expanded]:not(:disabled)' ) || [];
  }

  /**
   * Set the top level items of visible navigations ONLY.
   */
  setTopLevelItems() {
    const visibleNavigations = this.navigations?.filter( (nav, index) => this.displaysNavigations[index] );

    this.topLevelItems = visibleNavigations?.reduce( (acc, nav) => {

      const items = this.isMobile
        ? nav?.querySelectorAll( ':scope > ul > li > a[href], :scope > ul > li > button[aria-expanded]:not(:disabled)' )
        : [...this.getNavigationSiblings( nav?.previousElementSibling ), ...nav?.querySelectorAll( ':scope > ul > li > a[href], :scope > ul > li > button[aria-expanded]:not(:disabled)' ), ...this.getNavigationSiblings( nav?.nextElementSibling )];
      if (!items || !items.length) return acc;

      return acc.concat( Array.from( items ) );
    }, [] );

    this.subMenuToggles = visibleNavigations?.reduce( (acc, nav) => {
      const items = nav?.querySelectorAll( 'button[aria-expanded]:not(:disabled)' )

      if (!items || !items.length) return acc;

      return acc.concat( Array.from( items ) );
    }, [] );
  }

  /**
   *
   * @param {array} elements Array of elements to trap.
   * @param {HTMLElement} currentElement
   * @param {boolean} isShift Is the shift key pressed?
   */
  setFocusTrap(elements = [], currentElement = null, isShift = false) {
    if (!elements || !elements.length) {
      return;
    }

    if (!currentElement) {
      currentElement = document.activeElement;
    }

    let firstElement, lastItem;

    if (this.isMobile && this.isOpen) {
      const lastIndex = elements.length - 1;

      firstElement = elements[0],
        lastItem = elements[lastIndex];

      // detect if I'm currently tabbing.
      window.addEventListener( 'keydown', (e) => {
        const {target, code, shiftKey} = e;
        const index = elements.indexOf( target );
        if (!shiftKey && 'Tab' === code && index === lastIndex) {
          e.preventDefault();
          firstElement.focus();
        } else if (shiftKey && 'Tab' === code && 0 === index) {
          e.preventDefault();
          lastItem.focus();
        }
      } );

    } else if (this.subMenuIsOpen && this.subMenuItems.length) {
      firstElement = this.subMenuItems[0],
        lastItem = this.subMenuItems[this.subMenuItems.length - 1];

      if (!firstElement || !lastItem) {
        return;
      }

      if (isShift && currentElement === firstElement) {
        // Tabbing backwards, to the last item.
        lastItem.focus();
      } else if (currentElement === lastItem) {
        // Back to the first item.
        firstElement.focus();
      }
    }
  }

  /**
   * KeyDown handler for the navigation.
   *
   * @param {object} event The event object.
   * @param {number} depth The Depth of the current list.
   */
  keyDownHandler(event, depth = 0) {
    const {target, code, shiftKey} = event;

    const list = this.getListByDepth( depth );

    switch (code) {
      case 'ArrowDown':
        event.preventDefault();
        if (0 === depth && !this.isMobile) {
          // Horizontal Menubar: Use ArrowDown to open the submenu and focus on the first item.
          this.openSubMenu( event, true );
        }

        this.focusNextItem( target, depth );

        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousItem( target, depth );
        break;
      case 'ArrowLeft':
        if (0 === depth && !this.isMobile) {
          this.closeTopLevelAndFocusDifferentElement( target, true );
        } else if (depth > 0 && !this.isMobile) {
          this.closeTopLevelAndFocusDifferentElement( this.activeSubmenuToggle, true );
        } else if (this.isMobile) {
          if (0 === depth) {
            // Return focus to the menu toggle when on top-level items.
            this.controller.focus();
          } else {
            this.closeSubMenu();
          }
        }
        break;
      case 'ArrowRight':
        if (0 === depth && !this.isMobile) {
          this.closeTopLevelAndFocusDifferentElement( target, false );
        } else if( depth > 0 && ! this.isMobile) {
          this.closeTopLevelAndFocusDifferentElement( this.activeSubmenuToggle, false );
        } else if (0 === depth && this.isMobile) {
          // Open Submenu.
          this.openSubMenu( event, true );
        }
        break;
      case 'Space':
        event.preventDefault();
        target.click();
        break;

      case 'Tab':
        if (!this.isMobile && this.subMenuIsOpen) {
          const isLastItem = this.isLastSubmenuItem( target );
          const isFirstItem = this.isFirstSubmenuItem( target );

          if ((isLastItem && !shiftKey) || (isFirstItem && shiftKey)) {

            event.preventDefault();

            let nextItem = null; // Same as default closeSubMenu().
            if (!shiftKey) {
              const currentToggle = this.subMenuIsOpen.previousElementSibling;
              const topLevelIndex = this.topLevelItems.indexOf( currentToggle );

              nextItem = this.topLevelItems[topLevelIndex + 1] || this.topLevelItems[this.topLevelItems.length - 1];
            }

            this.closeSubMenu( nextItem );
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        // Check if the focused element is a top-level item
        if (!this.isMobile && list.includes( target )) {
          this.closeSubMenu( this.subMenuIsOpen?.previousElementSibling );
        } else {
          this.closeSubMenu();
          this.closeNavigation( this.isMobile );
        }
        break;
    }
  }

  /**
   * Event handler for the controller keydown event.
   *
   * @param {object} event The event object.
   */
  controllerKeyDownHandler(event) {
    if (!this.isOpen) {
      // Safeguarding against the controller being focused when the navigation is closed.
      return;
    }

    const {code} = event;

    switch (code) {
      case 'ArrowDown':
        event.preventDefault();
        this.openNavigation( true );
        this.topLevelItems[0].focus(); // Apparently we need to focus the first item.
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.openNavigation( true );
        this.topLevelItems[this.topLevelItems.length - 1].focus(); // Apparently we need to focus the last one.
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        // On mobile devices, users generally expect arrow keys to only control the menu when it's open.
        // When the menu is closed, lateral navigation between menu toggles could be confusing.
        // The current implementation might cause unexpected focus jumps on mobile layouts.
        if (this.isMobile && this.isOpen) {
          this.closeNavigation( false );
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeNavigation();
        this.controller.focus();
        break;
    }
  }

  /**
   * Close the top level and fucs a different element.

   * @param {HTMLElement} menuItem
   * @param {boolean} focusPreviousElement Focus previous or next element.
   *
   * @return {boolean}
   */
  closeTopLevelAndFocusDifferentElement(menuItem, focusPreviousElement = false) {
    const index = this.topLevelItems.indexOf( menuItem );

    if (index > -1 && index <= this.topLevelItems.length - 1) {
      if (!focusPreviousElement && index < this.topLevelItems.length - 1) {
        this.topLevelItems[index + 1].focus();
      } else if (focusPreviousElement && index > 0) {
        this.topLevelItems[index - 1].focus();
      } else if (focusPreviousElement && index === 0) {
        // chatGPT says I've to do the following:
        // ArrowLeft: Move focus to the previous menu item. If the focus is on the first item, the focus should wrap around to the last menu item (circular navigation).
        this.topLevelItems[this.topLevelItems.length - 1].focus();
      } else if (!focusPreviousElement && index === this.topLevelItems.length - 1) {
        // chatGPT says I've to do the following:
        // ArrowRight: Move focus to the first menu item. If the focus is on the last item, the focus should wrap around to the first menu item (circular navigation).
        this.topLevelItems[0].focus();
      }

      if (this.isMobile) {
        this.isOpen = false;
      } else {
        const toggle = this.subMenuIsOpen.previousElementSibling;
        if (toggle) {
          toggle.ariaExpanded = false;
        }
      }

      return true;
    }

    return false;
  }


  /**
   * Get the list of items by depth.
   *
   * @param {number} depth the depth of the list.
   * @return {[]|*[]}
   */
  getListByDepth(depth = 0) {
    switch (depth) {
      case 0:
        return this.topLevelItems;
      case 1:
        return this.subMenuItems || [];
    }
  }

  /**
   * Get the zoom level.
   * @return {number} The zoom level.
   */
  getZoomLevel() {
    return Math.round(window.devicePixelRatio * 100);
  }

  updateTabIndex() {
    if (this.isMobile) {
      this.topLevelItems?.forEach( item => item.tabIndex = this.isOpen ? 0 : -1 );
    } else {
      // Re-enable them.
      this.topLevelItems?.forEach( item => item.tabIndex = 0 );
    }
  }

  /**
   * Toggle the navigation.
   *
   * @param {boolean} focusTrap Should the focus trap be enabled?
   */
  toggleNavigation(focusTrap = false) {
    !this.isOpen ? this.openNavigation( focusTrap ) : this.closeNavigation();

    if (focusTrap) {
      this.setFocusTrap( this.getListByDepth( 0 ) );
    }
  }

  /**
   * Close the navigation.
   * @param {boolean} setFocus Set focus on the control. Might be set to false from dispatchedEvents.
   */
  closeNavigation(setFocus = true) {
    this.isOpen = false;

    this.updateController( false );

    if (!this.isMobile) {
      this.closeSubMenu();
    }

    if (setFocus) {
      setTimeout( () => this.controller.focus(), 1); // Delay the focus to prevent the focus is migrated to the search-toggle.
    }

    this.updateTabIndex();
  }

  /**
   * Open the navigation.
   * @param {boolean} focusTrap Should the focus trap be enabled?
   */
  openNavigation(focusTrap = false) {
    if (this.isOpen) return;

    this.isOpen = true;
    this.updateController( true );
    this.updateTabIndex();

    if (focusTrap) {
      this.setFocusTrap( this.getListByDepth( 0 ) );
    }

    window.dispatchEvent( new CustomEvent( "closeSearch" ) );
  }

  /**
   * Update the controller.
   *
   * @param {boolean} isOpen Is open or not.
   */
  updateController( isOpen = false ) {
    const {closeLabel, openLabel} = this.controller.dataset;

    this.controller.ariaExpanded = isOpen;
    this.controller.querySelector( 'span.sr-only' ).textContent = isOpen ? closeLabel : openLabel;
  }

  toggleSubMenu(event) {
    const {target} = event;

    const isOpening = 'false' === target.ariaExpanded

    target.ariaExpanded = isOpening; // Toggle the aria-expanded attribute, returns a string. Can be updated by a boolean.

    // Close all other sub-menus.
    if (isOpening) {
      this.openSubMenu( event, false );
    } else {
      this.subMenuIsOpen = false;
      this.subMenuItems = [];
      this.deleteSubmenuKeyboardEvents();
    }
  }

  /**
   * Open submenu.
   *
   * @param {object} event The event object.
   * @param {boolean} focusFirstElement Should the first element be focused?
   */
  openSubMenu(event, focusFirstElement = false) {
    const {target} = event;

    if ('BUTTON' !== target.nodeName) {
      return;
    }

    target.ariaExpanded = true; // Toggle the aria-expanded attribute, returns a string. Can be updated by a boolean.
    this.activeSubmenuToggle = target;

    this.subMenuToggles?.filter( toggle => toggle !== target ).forEach( toggle => toggle.ariaExpanded = false );
    this.subMenuIsOpen = target.nextElementSibling;

    this.subMenuItems = target.nextElementSibling.classList.contains( this.classList.megaMenu )
      // Mega menu, may contain multiple lists.
      ? Array.from( target.parentNode.querySelectorAll( ':scope > ul > li > a[href], :scope > ul > li > ul > li > a[href], :scope > ul > li > button:not(:disabled), :scope > ul > li > ul > li > button:not(:disabled)' ) )
      // Simple menu list.
      : this.subMenuItems = Array.from( target.parentNode.querySelectorAll( ':scope > ul > li > a[href], :scope > ul > li > button:not(:disabled)' ) );

    this.initSubmenuKeyboardEvents();

    if (focusFirstElement) {
      this.subMenuItems[0].focus();
    }
  }

  /**
   * Close the submenu and focus the toggle.
   *
   * @param {HTMLElement} focusToggle Element to focus instead of the default.
   */
  closeSubMenu(focusToggle = null) {
    if (!this.subMenuIsOpen) {
      return;
    }

    const activeSubmenuToggle = this.activeSubmenuToggle;
    if (activeSubmenuToggle) {
      activeSubmenuToggle.ariaExpanded = false;

      if (!focusToggle) {
        activeSubmenuToggle.focus();
      }
    }

    if (!!focusToggle) {
      setTimeout( () => focusToggle.focus(), 1 ); // Focus the toggle after the submenu is closed, set delay to prevent the focus is migrated to the search-toggle.
    }

    this.subMenuIsOpen = false;
    this.subMenuItems = [];
    this.activeSubmenuToggle = null;
    this.deleteSubmenuKeyboardEvents();
  }

  /**
   * Focus the next item in the list.
   * STAY WITHIN THE CURRENT (SUB)MENU.
   *
   * @param {HTMLElement} currentElement the current element that is focused.
   * @param {number} depth The depth of the current list item.
   */
  focusNextItem(currentElement, depth = 0) {
    if (this.isMobile && 0 === depth) {
      if ('BUTTON' === currentElement.nodeName && 'true' === currentElement.ariaExpanded) {
        // Focus first subMenuItem, the sub-menu is open.
        this.subMenuItems[0].focus();
      } else {
        // Proceed to next top-level item.
        const index = this.topLevelItems.indexOf( currentElement );
        // Focus the next top-level item, when the current element is the last item focus the last item.
        this.topLevelItems[index > -1 && index < this.topLevelItems.length - 1 ? index + 1 : 0].focus();
      }
    } else {
      if (!this.subMenuIsOpen || !this.subMenuItems.length || !this.subMenuItems.includes( currentElement )) {
        // Something went wrong.
        return;
      }

      const index = this.subMenuItems.indexOf( currentElement );
      this.subMenuItems[index > -1 && index === this.subMenuItems.length - 1 ? 0 : index + 1].focus();
    }
  }

  /**
   * Focus the previous item in the list.
   * STAY WITHIN THE CURRENT (SUB)MENU.
   *
   * @param {HTMLElement} currentElement the current element that is focused.
   * @param {number} depth The depth of the current list item.
   */
  focusPreviousItem(currentElement, depth = 0) {
    if (this.isMobile && 0 === depth) {
      const topLevelIndex = this.topLevelItems.indexOf( currentElement );
      // Focus the previous top-level item, when the current element is the first item focus the last item.
      this.topLevelItems[topLevelIndex > 0 && topLevelIndex <= this.topLevelItems.length - 1 ? topLevelIndex - 1 : this.topLevelItems.length - 1].focus();
    } else {
      if (!this.subMenuIsOpen || !this.subMenuItems.length || !this.subMenuItems.includes( currentElement )) {
        // Something went wrong.
        return;
      }

      const index = this.subMenuItems.indexOf( currentElement );
      this.subMenuItems[0 === index ? this.subMenuItems.length - 1 : index - 1].focus();
    }
  }

  /**
   * Checks if the menuItem is the last item in the submenu.
   *
   * @param {HTMLElement} menuItem The menu item to check the index from.
   * @return {boolean}
   */
  isLastSubmenuItem(menuItem) {
    return this.subMenuItems.indexOf( menuItem ) === this.subMenuItems.length - 1;
  }

  /**
   * Checks if the menuItem is the first item in the submenu.
   *
   * @param {HTMLElement} menuItem The menu item to check the index from.
   * @return {boolean}
   */
  isFirstSubmenuItem(menuItem) {
    return this.subMenuItems.indexOf( menuItem ) === 0;
  }

  getActiveSubmenuToggle() {
    return this.subMenuToggles?.find( btn => btn.ariaExpanded === 'true' );
  }
}

export default navigation;
